/**
 * Dashboard home page with key metrics and quick actions.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, ApiRequestError } from '@/lib/api'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { formatCurrency, formatNumber, formatPercent } from '@/lib/formatters'

interface HealthStatus {
  status: string
  services: {
    api: string
    database: string
    redis: string
  }
}

interface DashboardMetrics {
  orders: {
    total: number
    today: number
    pending: number
  }
  revenue: {
    total: number
    today: number
    currency: string
  }
  subscriptions: {
    active: number
    mrr: number
    churn_rate: number
  }
}

function MetricCard({
  title,
  value,
  description,
  loading,
  error,
}: {
  title: string
  value: string | number
  description?: string
  loading?: boolean
  error?: string | null
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="animate-pulse h-8 bg-muted rounded w-24" />
        ) : error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : (
          <>
            <div className="text-2xl font-semibold">{value}</div>
            {description && (
              <p className="text-xs text-muted-foreground">{description}</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

export default function DashboardPage() {
  const { user, hasRole } = useAuth()
  const [health, setHealth] = useState<HealthStatus | null>(null)
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null)
  const [loading, setLoading] = useState(true)
  const [metricsLoading, setMetricsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [metricsError, setMetricsError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchHealth() {
      try {
        const response = await api.get<HealthStatus>('/health')
        setHealth(response.data)
      } catch (err) {
        if (err instanceof ApiRequestError) {
          setError(err.message)
        } else {
          setError('Failed to fetch health status')
        }
      } finally {
        setLoading(false)
      }
    }

    async function fetchMetrics() {
      try {
        const response = await api.get<DashboardMetrics>('/dashboard/metrics')
        setMetrics(response.data)
      } catch (err) {
        if (err instanceof ApiRequestError) {
          setMetricsError(err.message)
        } else {
          setMetricsError('Failed to fetch metrics')
        }
      } finally {
        setMetricsLoading(false)
      }
    }

    fetchHealth()
    fetchMetrics()
  }, [])

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground mt-2">
          Welcome back, {user?.name}
        </p>
      </div>

      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8">
        <MetricCard
          title="Total Commerce Orders"
          value={metrics ? formatNumber(metrics.orders.total) : '-'}
          description={metrics ? `${metrics.orders.today} today` : undefined}
          loading={metricsLoading}
          error={metricsError}
        />
        <MetricCard
          title="Pending Orders"
          value={metrics ? formatNumber(metrics.orders.pending) : '-'}
          description="Awaiting payment"
          loading={metricsLoading}
          error={metricsError}
        />
        <MetricCard
          title="Total Revenue"
          value={metrics ? formatCurrency(metrics.revenue.total, metrics.revenue.currency) : '-'}
          description={metrics ? `${formatCurrency(metrics.revenue.today, metrics.revenue.currency)} today` : undefined}
          loading={metricsLoading}
          error={metricsError}
        />
        <MetricCard
          title="Active Subscriptions"
          value={metrics ? formatNumber(metrics.subscriptions.active) : '-'}
          description={metrics ? `MRR: ${formatCurrency(metrics.subscriptions.mrr, 'CNY')}` : undefined}
          loading={metricsLoading}
          error={metricsError}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {/* System Health Card */}
        {hasRole('admin') && (
          <Card>
            <CardHeader>
              <CardTitle>System Health</CardTitle>
              <CardDescription>Current status of all services</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-muted-foreground">Loading...</p>
              ) : error ? (
                <p className="text-destructive">{error}</p>
              ) : health ? (
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Overall</span>
                    <span className={health.status === 'healthy' ? 'text-green-600' : 'text-yellow-600'}>
                      {health.status}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>API</span>
                    <span className={health.services.api === 'up' ? 'text-green-600' : 'text-red-600'}>
                      {health.services.api}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Database</span>
                    <span className={health.services.database === 'up' ? 'text-green-600' : 'text-red-600'}>
                      {health.services.database}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Redis</span>
                    <span className={health.services.redis === 'up' ? 'text-green-600' : 'text-red-600'}>
                      {health.services.redis}
                    </span>
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>
        )}

        {/* Quick Actions Card */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Common tasks and shortcuts</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {hasRole('admin') && (
              <Button asChild className="w-full" variant="outline">
                <Link to="/applications">Manage Applications</Link>
              </Button>
            )}
            <Button asChild className="w-full" variant="outline">
              <Link to="/orders">View Orders</Link>
            </Button>
            <Button asChild className="w-full" variant="outline">
              <Link to="/products">Manage Products</Link>
            </Button>
          </CardContent>
        </Card>

        {/* Subscription Metrics */}
        <Card>
          <CardHeader>
            <CardTitle>Subscription Metrics</CardTitle>
            <CardDescription>Key subscription performance</CardDescription>
          </CardHeader>
          <CardContent>
            {metricsLoading ? (
              <p className="text-muted-foreground">Loading...</p>
            ) : metricsError ? (
              <p className="text-destructive">{metricsError}</p>
            ) : metrics ? (
              <div className="space-y-4">
                <div>
                  <div className="text-sm text-muted-foreground">Monthly Recurring Revenue</div>
                  <div className="text-xl font-semibold">
                    {formatCurrency(metrics.subscriptions.mrr, 'CNY')}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Churn Rate</div>
                  <div className="text-xl font-semibold">
                    {formatPercent(metrics.subscriptions.churn_rate)}
                  </div>
                </div>
                <Button asChild variant="outline" size="sm" className="w-full">
                  <Link to="/subscriptions">View All Subscriptions</Link>
                </Button>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
